import React from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, Users, Radio, ScrollText, LogOut, Loader2, Mail, Inbox, Tag, CreditCard, Activity, ShieldCheck, Send } from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuth';
import { Logo } from '@/components/Logo';
import { Seo } from '@/components/Seo';
import { ADMIN } from '@/constants/testIds';

const LINKS = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, testId: ADMIN.navDashboard },
  { to: '/admin/subscribers', label: 'Subscribers', icon: Mail, testId: ADMIN.navSubscribers },
  { to: '/admin/inbox', label: 'Contact Forms', icon: Inbox, testId: ADMIN.navInbox },
  { to: '/admin/users', label: 'Users', icon: Users, testId: ADMIN.navUsers },
  { to: '/admin/providers', label: 'Providers', icon: Radio, testId: ADMIN.navProviders },
  { to: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard, testId: ADMIN.navSubscriptions },
  { to: '/admin/mt5', label: 'MT5', icon: Activity, testId: ADMIN.navMt5 },
  { to: '/admin/plans', label: 'Plans', icon: Tag, testId: ADMIN.navPlans },
  { to: '/admin/mailbox', label: 'Mailbox', icon: Mail, testId: ADMIN.navMailbox },
  { to: '/admin/marketing', label: 'Marketing', icon: Send, testId: ADMIN.navMarketing },
  { to: '/admin/team', label: 'Team', icon: ShieldCheck, testId: ADMIN.navTeam },
  { to: '/admin/audit', label: 'Audit Log', icon: ScrollText, testId: ADMIN.navAudit },
];

const ROLE_COLORS = { owner: 'text-brand', admin: 'text-brand', support: 'text-success', finance: 'text-yellow-400', 'read-only': 'text-white/50' };

export const AdminLayout = () => {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();

  if (admin === undefined) {
    return <div className="flex min-h-screen items-center justify-center bg-ink"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  }
  if (admin === null) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => { await logout(); navigate('/admin/login'); };

  return (
    <div className="flex min-h-screen bg-ink text-white">
      <Seo title="Admin Console" description="Copy.Click internal admin console" noindex />
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-white/10 bg-surface/60 backdrop-blur-xl lg:flex">
        <div className="flex h-16 items-center border-b border-white/10 px-6"><Logo textClass="text-lg" /></div>
        <nav className="flex-1 space-y-1 p-4">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-brand/10 text-brand' : 'text-white/60 hover:bg-white/5 hover:text-white'}`}
            >
              <l.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3 rounded-xl bg-white/5 px-4 py-3">
            <p className="truncate text-sm font-medium text-white">{admin.name}</p>
            <p className="truncate text-xs text-white/40">{admin.email}</p>
            <span className={`mt-1 inline-block text-xs font-semibold uppercase tracking-wider ${ROLE_COLORS[admin.role] || 'text-white/50'}`}>{admin.role}</span>
          </div>
          <button onClick={doLogout} data-testid={ADMIN.logout} className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-white/60 transition-colors hover:bg-destructive/10 hover:text-destructive">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-white/10 bg-surface/80 px-4 backdrop-blur-xl lg:hidden">
        <Logo textClass="text-base" />
        <button onClick={doLogout} className="text-white/60"><LogOut className="h-5 w-5" /></button>
      </div>

      <main className="flex-1 px-5 pb-16 pt-20 sm:px-8 lg:ml-64 lg:pt-8">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t border-white/10 bg-surface/90 py-2 backdrop-blur-xl lg:hidden">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] ${isActive ? 'text-brand' : 'text-white/50'}`}>
            <l.icon className="h-5 w-5" /> {l.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
